import { useMemo } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { clsx } from 'clsx';

import { DISTRIBUTOR_NAME } from '@autopass-web/domain';

import { PATH } from '../../../config';
import { CURRENT_DISTRIBUTOR } from '../../../new-distributor-config';
import useAppStore from '../../../pages/use-app';

import type {
  BasePoiCharacteristics,
  FilterOptions,
  PinType,
} from '@autopass-web/feature-maps';
import type { GasStation, Parkinglot, Poi } from '@autopass-web/types';

const BOTTOM_NAV_BAR_HEIGHT = 60;

const FeatureMaps = dynamic(() => import('@autopass-web/feature-maps'), {
  ssr: false,
});

export const CoreMapPage = () => {
  const { me, licensePlates } = useAppStore();
  const { push } = useRouter();
  const isAutoSyncLicensePlateMochipass =
    me?.autoSyncLicensePlateMochipass ?? false;

  const creditCardInfo = useMemo(
    () =>
      me?.currentCreditCard
        ? {
            lastFourCode: me.currentCreditCard.lastFourCode,
            bankName: me.currentCreditCard.bank.name,
          }
        : undefined,
    [me?.currentCreditCard],
  );

  const refuelingAutopassLicensePlates = useMemo(
    () => licensePlates.filter((licensePlate) => licensePlate.mochipassEnable),
    [licensePlates],
  );

  return (
    <div className={clsx('h-full', 'w-full')}>
      <FeatureMaps
        basePath="/maps"
        isNextModule
        events={{
          distributorName: DISTRIBUTOR_NAME[CURRENT_DISTRIBUTOR],
        }}
        poiCardTutorialConfig={{
          isExternallyNavigable: true,
          numberOfLicensePlates: licensePlates.length,
          numberOfRefuelingAutopassLicensePlates:
            isAutoSyncLicensePlateMochipass
              ? refuelingAutopassLicensePlates.length
              : 0,
          creditCard: creditCardInfo,
          onLicensePlatePageNavigate: () => {
            push(PATH.LICENSE_PLATES);
          },
          onPaymentPageNavigate: () => {
            push(PATH.PAYMENT);
          },
          onRefuelingAutopassNavigate: () => {
            push(PATH.MY_MOCHIPASS);
          },
          onBarcodePaymentNavigate: () => {
            push(PATH.BARCODE_PAYMENT);
          },
        }}
        parsePinType={parsePinType}
        basePoiCharacteristics={MAP_BASE_POI_CHARACTERISTICS}
        customFilterOptions={MAP_CUSTOM_FILTER_OPTIONS}
        bottomAdditionalOffset={BOTTOM_NAV_BAR_HEIGHT}
      />
    </div>
  );
};

const MAP_BASE_POI_CHARACTERISTICS = {
  gasStation: {
    cooperationStates: ['in_cooperated'],
  },
  parkinglot: {
    cooperationStates: ['not_in_cooperated', 'coming_soon', 'in_cooperated'],
  },
} as BasePoiCharacteristics;

const MAP_CUSTOM_FILTER_OPTIONS = {
  gasStation: {
    refuelingServices: ['mochipay', 'autopass', 'qrcode'],
    oilTypes: ['gas_92', 'gas_95', 'gas_98', 'diesel'],
    vehicleTypes: ['car', 'moto'],
    oilSuppliers: ['CPC', 'FPC'],
  },
  parkinglot: {
    parkingServices: ['autopass'],
    vehicleTypes: ['car', 'moto'],
    payTaipeiCategories: ['pay_taipei_no_card_entry_exit'],
  },
} as FilterOptions;

function parsePinType(poi: Poi): PinType {
  switch (poi.type) {
    case 'GasStation':
      return parseGasStationPinType(poi);
    case 'Parkinglot':
      return parseParkinglotPinType(poi);
    default:
      throw new Error('parsePinType: Invalid poi type');
  }
}

function parseParkinglotPinType(parkinglot: Parkinglot) {
  const { cooperation_state } = parkinglot;

  if (
    cooperation_state === 'not_in_cooperated' ||
    cooperation_state === 'coming_soon'
  )
    return 'parkinglot';
  return 'parkinglot-autopass';
}

function parseGasStationPinType(gasStation: GasStation) {
  const { cooperation_state, supplier } = gasStation;
  const paymentType = parseGasStationPaymentType(gasStation);

  if (
    cooperation_state === 'not_in_cooperated' ||
    cooperation_state === 'coming_soon'
  )
    return 'gas-station';

  switch (paymentType) {
    case 'autopass': {
      if (supplier === 'CPC') {
        return 'gas-station-autopass-cpc';
      }
      if (supplier === 'FPC') {
        return 'gas-station-autopass-fpc';
      }

      return 'gas-station-autopass';
    }
    case 'qrcode': {
      if (supplier === 'CPC') {
        return 'gas-station-qrcode-cpc';
      }
      if (supplier === 'FPC') {
        return 'gas-station-qrcode-fpc';
      }
      return 'gas-station-qrcode';
    }
    case 'mochipay': {
      if (supplier === 'CPC') {
        return 'gas-station-mochipay-cpc';
      }
      if (supplier === 'FPC') {
        return 'gas-station-mochipay-fpc';
      }

      return 'gas-station-mochipay';
    }
    default:
      return 'gas-station-mochipay';
  }
}

function parseGasStationPaymentType(gasStation: GasStation) {
  if (checkIsGasStationSupportMochipassService(gasStation)) return 'autopass';
  if (checkIsGasStationSupportMochipayService(gasStation)) return 'mochipay';
  if (checkIsGasStationSupportBarcodeService(gasStation)) return 'qrcode';
  return 'none';
}

const checkIsGasStationSupportBarcodeService = (poi) =>
  poi.support_refueling_services.includes('barcode');

const checkIsGasStationSupportMochipayService = (poi) =>
  poi.support_refueling_services.includes('mochipay');

const checkIsGasStationSupportMochipassService = (poi) =>
  poi.support_refueling_services.includes('mochipass');
